import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { fmtWh } from '../time';

// Tasks (Fernando 1.11, docs/TASKS.md): one-time warehouse tasks — scan a list, scan and move, check and fix, a
// custom picking / replenishing list, or one instruction to tick — with a record of who did what and how.
// Managers (or anyone with the P&P manager right) make tasks; everyone with the `tasks` right does them.

export interface TaskRow {
  id: number; title: string; instruction: string | null; kind: string; status: string; priority: number;
  for_person: string | null; source: string; created_by: string; created_at: string; started_by: string | null;
  started_at: string | null; done_by: string | null; done_at: string | null; done_note: string | null;
  cancelled_by: string | null; cancelled_at: string | null; cancel_note: string | null;
  total: number; done: number; skipped: number;
}

export const KIND_TEXT: Record<string, { label: string; hint: string }> = {
  scan: { label: 'Scan list', hint: 'scan every piece on the list — is it there?' },
  move: { label: 'Scan & move', hint: 'choose the shelf, then scan every piece — each one goes there' },
  check: { label: 'Check & fix', hint: 'go through the points, tick each, note what you fixed' },
  pick: { label: 'Pick / replenish list', hint: 'walk the list in location order and pick the pieces' },
  tick: { label: 'One thing to do', hint: 'read it, do it, press Done' },
};

const minutes = (a: string | null, b: string | null) => {
  if (!a || !b) return null;
  const m = Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

export default function TasksPage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState<TaskRow[]>([]);
  const [closed, setClosed] = useState<TaskRow[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', instruction: '', kind: 'tick', priority: 0, for_person: '', items: '' });
  const [busy, setBusy] = useState(false);
  // open_task: while an Ä → Å task is open the button opens it (its new pieces are added) — never a second task (3.11)
  const [aaPreview, setAaPreview] = useState<{ products: number; pieces: number; sample: string[]; open_task: number | null } | null>(null);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/tasks`);
    if (!r.ok) { setErr('Could not load the tasks'); return; }
    const d = await r.json();
    setOpen(d.open || []); setClosed(d.closed || []); setCanManage(!!d.can_manage); setErr('');
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canManage) return;
    authFetch(`${API}/tasks/aa-move/preview`).then(async r => { if (r.ok) setAaPreview(await r.json()); }).catch(() => {});
  }, [canManage, open.length]);

  // items pasted one per line: "code" or "code x3" or "code x3 → Å 2" (a code, a quantity, a target shelf)
  const parseItems = (text: string) => text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    const m = l.match(/^(.*?)(?:\s+[x×]\s*(\d+))?(?:\s*(?:→|->)\s*(.+))?$/i);
    const code = (m?.[1] || l).trim();
    return form.kind === 'check' ? { label: code } : { code, qty: m?.[2] ? Number(m[2]) : 1, to_place: m?.[3]?.trim() || undefined };
  });
  const create = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const body = { ...form, for_person: form.for_person || null, items: parseItems(form.items) };
      const r = await authFetch(`${API}/tasks`, { method: 'POST', body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : 'Could not create the task'); return; }
      setCreating(false); setForm({ title: '', instruction: '', kind: 'tick', priority: 0, for_person: '', items: '' });
      await load();
      navigate(`/tasks/${d.id}`);
    } finally { setBusy(false); }
  };
  const makeAaMove = async () => {
    if (busy || !aaPreview) return;
    if (!aaPreview.open_task && !window.confirm(`Make the task "Move the returned Ä pieces to Å" with ${aaPreview.products} products (${aaPreview.pieces} pcs)?`)) return;
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/tasks/aa-move`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(typeof d.detail === 'string' ? d.detail : 'Could not make the task'); return; }
      await load();
      navigate(`/tasks/${d.id}`);
    } finally { setBusy(false); }
  };

  const Row = ({ t }: { t: TaskRow }) => (
    <button onClick={() => navigate(`/tasks/${t.id}`)} className="w-full text-left rounded-xl border border-gray-800 bg-[#1a1d27] hover:border-blue-500 p-4 transition-colors">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-white font-semibold">{t.title}</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-300">{KIND_TEXT[t.kind]?.label || t.kind}</span>
        {t.priority > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-700 text-white">priority</span>}
        {t.for_person && <span className="text-xs text-blue-300">for {t.for_person}</span>}
        <span className="ml-auto text-xs text-gray-500">#{t.id}</span>
      </div>
      <div className="mt-1 text-xs text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
        {t.kind !== 'tick' && <span>{t.done} of {t.total} done{t.skipped ? ` · ${t.skipped} not found` : ''}</span>}
        {t.status === 'in_progress' && <span className="text-amber-300">in progress — {t.started_by} since {fmtWh(t.started_at!, 'datetime')}</span>}
        {t.status === 'open' && <span>made by {t.created_by} {fmtWh(t.created_at, 'datetime')}</span>}
        {t.status === 'done' && <span className="text-emerald-300">done by {t.done_by} {fmtWh(t.done_at!, 'datetime')}{minutes(t.started_at, t.done_at) ? ` · ${minutes(t.started_at, t.done_at)}` : ''}{t.done_note ? ` · "${t.done_note}"` : ''}</span>}
        {t.status === 'cancelled' && <span className="text-red-300">cancelled by {t.cancelled_by}{t.cancel_note ? ` · "${t.cancel_note}"` : ''}</span>}
      </div>
    </button>
  );

  return (
    <div className="p-6 text-gray-200 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-baseline gap-4 mb-1">
        <h1 className="text-2xl font-bold text-white">Tasks</h1>
        <span className="text-gray-500 text-sm">one-time jobs in the warehouse — who did what, and how</span>
        {canManage && (
          <span className="ml-auto flex gap-2">
            {aaPreview && (aaPreview.products > 0 || aaPreview.open_task) && (
              <button onClick={makeAaMove} disabled={busy} className="px-3 py-1.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-40"
                title={aaPreview.sample.join(', ')}>
                {aaPreview.open_task
                  ? `Open the Ä → Å task #${aaPreview.open_task}${aaPreview.products ? ` (+${aaPreview.products} new, ${aaPreview.pieces} pcs)` : ''}`
                  : `Make the Ä → Å move list (${aaPreview.products} products, ${aaPreview.pieces} pcs)`}
              </button>
            )}
            <button onClick={() => setCreating(c => !c)} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold">+ New task</button>
          </span>
        )}
      </div>
      {err && <div className="mb-3 text-red-400 text-sm">{err}</div>}

      {creating && (
        <div className="rounded-xl border border-blue-800 bg-[#1a1d27] p-4 mb-6 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-gray-300">Title
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white" placeholder="Check the Packeta cart labels" /></label>
            <label className="text-xs text-gray-300">Kind
              <select value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))} className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white">
                {Object.entries(KIND_TEXT).map(([k, v]) => <option key={k} value={k}>{v.label} — {v.hint}</option>)}
              </select></label>
          </div>
          <label className="block text-xs text-gray-300">Instruction (what to do, in the person's words)
            <textarea value={form.instruction} onChange={e => setForm(f => ({ ...f, instruction: e.target.value }))} rows={2} className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white" /></label>
          {form.kind !== 'tick' && (
            <label className="block text-xs text-gray-300">{form.kind === 'check' ? 'Points to check, one per line' : 'Items, one per line: a barcode, variant id or article number — optionally "x3" and "→ Å 2"'}
              <textarea value={form.items} onChange={e => setForm(f => ({ ...f, items: e.target.value }))} rows={6} className="mt-1 w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white font-mono text-sm"
                placeholder={form.kind === 'check' ? 'Zebra at station 1 prints straight\nPacketa cart has 2 empty bags' : '4743348817669 x2 → Å 1\n702559119-6XL'} /></label>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs text-gray-300">For (optional)
              <input value={form.for_person} onChange={e => setForm(f => ({ ...f, for_person: e.target.value }))} className="ml-2 w-40 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white" placeholder="anyone" /></label>
            <label className="text-xs text-gray-300 flex items-center gap-2"><input type="checkbox" checked={form.priority > 0} onChange={e => setForm(f => ({ ...f, priority: e.target.checked ? 1 : 0 }))} /> priority</label>
            <button onClick={create} disabled={busy || !form.title.trim()} className="ml-auto px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-semibold">Create</button>
            <button onClick={() => setCreating(false)} className="px-3 py-2 rounded-lg bg-gray-700 text-white">Cancel</button>
          </div>
        </div>
      )}

      <h2 className="text-white font-semibold mb-2">Open ({open.length})</h2>
      <div className="space-y-2 mb-8">
        {open.map(t => <Row key={t.id} t={t} />)}
        {open.length === 0 && <div className="text-gray-500 text-sm">Nothing open.</div>}
      </div>
      <h2 className="text-white font-semibold mb-2">Done and cancelled (last 30 days)</h2>
      <div className="space-y-2">
        {closed.map(t => <Row key={t.id} t={t} />)}
        {closed.length === 0 && <div className="text-gray-500 text-sm">None yet.</div>}
      </div>
    </div>
  );
}
